import React from 'react';

/** Single shimmer bar — uses enhanced shimmer animation */
export const SkeletonLine: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`shimmer rounded ${className}`} />
);

/** Card-shaped skeleton with configurable rows */
export const SkeletonCard: React.FC<{
  rows?: number;
  className?: string;
  showHeader?: boolean;
}> = ({ rows = 3, className = '', showHeader = true }) => (
  <div className={`bg-wiz-card border border-wiz-border rounded-xl p-5 ${className}`}>
    {showHeader && (
      <div className="flex items-center justify-between mb-4">
        <SkeletonLine className="h-4 w-32" />
        <SkeletonLine className="h-3 w-16" />
      </div>
    )}
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center gap-3" style={{ animationDelay: `${i * 0.05}s` }}>
          <SkeletonLine className="w-8 h-8 rounded-lg shrink-0" />
          <div className="flex-1 space-y-1.5">
            <SkeletonLine className={`h-3 ${i % 2 === 0 ? 'w-3/4' : 'w-1/2'}`} />
            <SkeletonLine className="h-2.5 w-1/3" />
          </div>
          <SkeletonLine className="h-5 w-12 rounded-full" />
        </div>
      ))}
    </div>
  </div>
);

/** Stat number skeleton with accent stripe */
export const SkeletonStat: React.FC<{ className?: string }> = ({ className = '' }) => (
  <div className={`bg-wiz-card border border-wiz-border rounded-xl p-5 card-accent-purple overflow-hidden ${className}`}>
    <SkeletonLine className="h-3 w-20 mb-3" />
    <SkeletonLine className="h-8 w-16 mb-2" />
    <SkeletonLine className="h-2.5 w-24" />
  </div>
);

/** Full-page skeleton grid — use in dashboards while API loads */
export const PageSkeleton: React.FC<{ title?: string }> = ({ title }) => (
  <div className="p-6 space-y-5">
    {title && (
      <div className="flex items-center justify-between">
        <div>
          <SkeletonLine className="h-6 w-48 mb-1.5" />
          <SkeletonLine className="h-3.5 w-72" />
        </div>
        <SkeletonLine className="h-9 w-28 rounded-lg" />
      </div>
    )}
    {/* Score section */}
    <div className="card-elevated rounded-xl p-6 flex items-center gap-8">
      <div className="w-[140px] h-[140px] rounded-full shimmer shrink-0" />
      <div className="flex-1 space-y-3">
        <SkeletonLine className="h-5 w-40" />
        <SkeletonLine className="h-3 w-80" />
        <div className="grid grid-cols-4 gap-3 mt-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="bg-wiz-card/50 rounded-lg px-3 py-2">
              <SkeletonLine className="h-5 w-8 mb-1" />
              <SkeletonLine className="h-2.5 w-12" />
            </div>
          ))}
        </div>
      </div>
    </div>
    {/* Stat row */}
    <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-8 gap-4 stagger-children">
      {Array.from({ length: 8 }).map((_, i) => <SkeletonStat key={i} />)}
    </div>
    {/* Two-column cards */}
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 stagger-children">
      <SkeletonCard rows={5} />
      <SkeletonCard rows={5} />
    </div>
    <SkeletonCard rows={8} />
  </div>
);

/** Table row skeleton */
export const SkeletonRow: React.FC<{ cols?: number }> = ({ cols = 5 }) => (
  <div className="flex items-center gap-4 px-4 py-3 border-b border-wiz-border/60">
    {Array.from({ length: cols }).map((_, i) => (
      <SkeletonLine key={i} className={`h-3 ${i === 0 ? 'w-8' : i === cols - 1 ? 'w-16' : 'flex-1'}`} />
    ))}
  </div>
);

/** Crossfade wrapper: shows skeleton, then crossfades to children when loaded */
export const ContentCrossfade: React.FC<{
  loading: boolean;
  skeleton: React.ReactNode;
  children: React.ReactNode;
}> = ({ loading, skeleton, children }) => (
  <div className="relative">
    <div className={`transition-all duration-300 ${loading ? 'opacity-100' : 'opacity-0 absolute inset-0 pointer-events-none'}`}>
      {skeleton}
    </div>
    <div className={`transition-all duration-300 ${loading ? 'opacity-0' : 'opacity-100 animate-content-reveal'}`}>
      {!loading && children}
    </div>
  </div>
);

export default SkeletonCard;
